import React from 'react';
import { AlertTriangle, Flame, DoorOpen, Phone, Users, ShieldAlert } from 'lucide-react';

const STEPS = [
  { icon: '🔔', text: 'Raise the alarm — shout "FIRE!" and break the nearest red call point glass' },
  { icon: '📞', text: 'Call 999 — "Fire at The Rattlebone, Sherston, Wiltshire"' },
  { icon: '🚪', text: 'Direct customers calmly to the nearest green fire exit sign' },
  { icon: '🚶', text: 'Leave immediately — do not collect belongings' },
  { icon: '🚪', text: 'Close all doors behind you to slow fire spread' },
  { icon: '📍', text: 'Assemble at the meeting point and await your manager' },
];

const EXTINGUISHERS = [
  { color: 'bg-red-600', label: 'Red — Water', use: 'Paper, wood, fabric', avoid: 'Electrics, fat' },
  { color: 'bg-yellow-500', label: 'Cream — Foam', use: 'Flammable liquids', avoid: 'Electrical' },
  { color: 'bg-zinc-800', label: 'Black — CO₂', use: 'Electrical equipment', avoid: 'Fat fires' },
  { color: 'bg-blue-600', label: 'Blue — Powder', use: 'Most fires (last resort)', avoid: 'Enclosed spaces' },
  { color: 'bg-yellow-600', label: 'Yellow — Wet Chem', use: 'Cooking oil & fat', avoid: 'Electrics' },
];

export default function FireSafetyInfo() {
  return (
    <div className="my-4 space-y-3">
      {/* Emergency banner */}
      <div className="border border-destructive/40 bg-destructive/5 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Flame className="w-5 h-5 text-destructive shrink-0" />
          <h3 className="text-sm font-bold text-foreground">On Discovering a Fire — Do This Immediately</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {STEPS.map((step, i) => (
            <div key={i} className="flex items-start gap-2 bg-background/60 rounded-lg px-3 py-2">
              <span className="text-base shrink-0">{step.icon}</span>
              <p className="text-xs text-foreground leading-relaxed">{step.text}</p>
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-start gap-2 bg-destructive/10 rounded-lg px-3 py-2 border border-destructive/20">
          <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
          <p className="text-xs font-semibold text-destructive">Never re-enter the building until the fire brigade confirms it is safe.</p>
        </div>
      </div>

      {/* Fire doors */}
      <div className="border border-border rounded-xl p-4 bg-card">
        <div className="flex items-center gap-2 mb-2">
          <DoorOpen className="w-4 h-4 text-primary" />
          <h4 className="text-xs font-bold text-foreground uppercase tracking-wider">Fire Doors & Evacuation Plans</h4>
        </div>
        <ul className="space-y-1.5">
          {[
            'Evacuation plans are posted on the back of all main entrance doors — study them in your first week',
            'Fire doors must remain CLOSED at all times — never prop them open',
            'A propped fire door is a serious health & safety violation — report it immediately',
            'All fire exits must be unobstructed at all times — report any blockages immediately',
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="text-primary mt-0.5">•</span>
              <p className="text-xs text-muted-foreground">{item}</p>
            </li>
          ))}
        </ul>
      </div>

      {/* Extinguisher guide */}
      <div className="border border-border rounded-xl p-4 bg-card">
        <div className="flex items-center gap-2 mb-3">
          <ShieldAlert className="w-4 h-4 text-primary" />
          <h4 className="text-xs font-bold text-foreground uppercase tracking-wider">Fire Extinguisher Colour Guide</h4>
        </div>
        <div className="space-y-1.5">
          {EXTINGUISHERS.map(e => (
            <div key={e.label} className="flex items-center gap-3">
              <div className={`w-5 h-5 rounded-full shrink-0 ${e.color}`} />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-foreground">{e.label}</p>
                <p className="text-[11px] text-muted-foreground">Use on: {e.use} · Avoid: {e.avoid}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 bg-destructive/5 border border-destructive/20 rounded-lg px-3 py-2">
          <p className="text-xs font-semibold text-destructive">⚠️ Never use water on electrical fires or burning fat/oil — this can be fatal.</p>
        </div>
      </div>
    </div>
  );
}
